import React from 'react';
import { Bell, Search } from 'lucide-react';
import { ViewMode, urlParams } from '../utils/urlParams';

interface BottomNavProps {
  currentView: ViewMode;
  inboxUnreadCount: number;
}

const INACTIVE_COLOR = '#132B64';
const ACTIVE_COLOR = '#22c55e';

const ACTIVE_FILTER = 'brightness(0) saturate(100%) invert(60%) sepia(80%) saturate(400%) hue-rotate(90deg)';
const INACTIVE_FILTER = 'brightness(0) saturate(100%) invert(12%) sepia(60%) saturate(700%) hue-rotate(195deg) brightness(80%)';

type TabDef = {
  label: string;
  iconType: 'lucide' | 'img';
  iconSrc?: string;
  lucideIcon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
};

export const BottomNav: React.FC<BottomNavProps> = ({ currentView, inboxUnreadCount }) => {
  const tabs: TabDef[] = [
    {
      label: 'Scout',
      iconType: 'lucide',
      lucideIcon: <Search size={24} />,
      active: currentView === 'all',
      onClick: () => urlParams.resetToHome(),
    },
    {
      label: 'My Horses',
      iconType: 'img',
      iconSrc: '/icons/horse_head_left_icon.svg',
      active: currentView === 'collection',
      onClick: () => {
        urlParams.updateUrlWithViewMode('collection');
        window.dispatchEvent(new CustomEvent('urlchange'));
      },
    },
    {
      label: 'Competitions',
      iconType: 'img',
      iconSrc: '/icons/flags_3180347.png',
      active: currentView === 'competitions',
      onClick: () => urlParams.navigateToCompetitions(),
    },
    {
      label: 'Account',
      iconType: 'img',
      iconSrc: '/icons/rider icon.svg',
      active: currentView === 'account-setting',
      onClick: () => urlParams.navigateToAccountSettings(),
    },
  ];

  const inboxActive = currentView === 'inbox' || currentView === 'inbox-detail';

  const renderIcon = (tab: TabDef, size?: string) => {
    const cls = size ?? 'h-6 w-6';
    if (tab.iconType === 'img' && tab.iconSrc) {
      return (
        <img
          src={tab.iconSrc}
          alt=""
          className={`${cls} object-contain`}
          style={{ filter: tab.active ? ACTIVE_FILTER : INACTIVE_FILTER }}
        />
      );
    }
    return tab.lucideIcon;
  };

  const renderBadge = () => {
    if (inboxUnreadCount <= 0) {
      return null;
    }

    return (
      <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
        {inboxUnreadCount > 99 ? '99+' : inboxUnreadCount}
      </span>
    );
  };

  const mobileBellButton = (
    <button
      key="Inbox"
      onClick={() => urlParams.navigateToInbox()}
      className="flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors"
      style={{ color: inboxActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
      aria-label="Inbox"
    >
      <span className="relative inline-flex">
        <Bell size={24} />
        {renderBadge()}
      </span>
      <span className="text-xs font-semibold tracking-wide">Inbox</span>
    </button>
  );

  const desktopBellButton = (
    <button
      key="Inbox"
      onClick={() => urlParams.navigateToInbox()}
      className={`flex items-center gap-3 mx-3 px-3 py-3 text-sm font-semibold rounded-lg transition-colors ${
        inboxActive ? 'bg-green-50' : 'hover:bg-gray-50'
      }`}
      style={{ color: inboxActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
    >
      <span className="relative inline-flex">
        <Bell className="h-5 w-5" />
        {renderBadge()}
      </span>
      <span>Inbox</span>
    </button>
  );

  return (
    <>
      {/* Mobile: bottom nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 50 }}
      >
        {tabs.slice(0, 3).map((tab) => (
          <button
            key={tab.label}
            onClick={tab.onClick}
            className="flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors"
            style={{ color: tab.active ? ACTIVE_COLOR : INACTIVE_COLOR }}
          >
            {renderIcon(tab)}
            <span className="text-xs font-semibold tracking-wide">{tab.label}</span>
          </button>
        ))}
        {mobileBellButton}
        {tabs.slice(3).map((tab) => (
          <button
            key={tab.label}
            onClick={tab.onClick}
            className="flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors"
            style={{ color: tab.active ? ACTIVE_COLOR : INACTIVE_COLOR }}
          >
            {renderIcon(tab)}
            <span className="text-xs font-semibold tracking-wide">{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* Desktop: left sidebar */}
      <nav
        className="hidden lg:flex flex-col fixed top-0 bottom-0 w-60 bg-white border-r border-gray-200"
        style={{ zIndex: 50, left: 'max(0px, calc((100vw - 1400px) / 2))' }}
      >
        <button
          onClick={() => urlParams.resetToHome()}
          className="flex items-center gap-2 px-6 py-5 hover:opacity-80 transition-opacity"
        >
          <img
            src="/logo topbred 1.png"
            alt="TopBred"
            className="h-8 w-8 object-contain"
          />
          <span
            className="text-black text-xl tracking-wide pt-1"
            style={{ fontFamily: 'Azonix, sans-serif' }}
          >
            TOPBRED
          </span>
        </button>

        <div className="flex flex-col mt-2">
          {tabs.slice(0, 3).map((tab) => (
            <button
              key={tab.label}
              onClick={tab.onClick}
              className={`flex items-center gap-3 mx-3 px-3 py-3 text-sm font-semibold rounded-lg transition-colors ${
                tab.active ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
              style={{ color: tab.active ? ACTIVE_COLOR : INACTIVE_COLOR }}
            >
              {renderIcon(tab, 'h-5 w-5')}
              <span>{tab.label}</span>
            </button>
          ))}
          {desktopBellButton}
          {tabs.slice(3).map((tab) => (
            <button
              key={tab.label}
              onClick={tab.onClick}
              className={`flex items-center gap-3 mx-3 px-3 py-3 text-sm font-semibold rounded-lg transition-colors ${
                tab.active ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
              style={{ color: tab.active ? ACTIVE_COLOR : INACTIVE_COLOR }}
            >
              {renderIcon(tab, 'h-5 w-5')}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};
